import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Package, Plus, Pencil, X, Trash2 } from "lucide-react";

const rp = (n) => "Rp " + new Intl.NumberFormat("id-ID").format(n || 0);
const EMPTY = { nama: "", harga: 0, unit: "", aktif: true, ukuran: [{ ukuran: "", stok: "" }] };

export default function KatalogManager() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const { data: items = [] } = useQuery({ queryKey: ["katalog", "all"], queryFn: async () => (await api.get("/katalog", { params: { include_inactive: true } })).data });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/katalog/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["katalog"] }); toast.success("Produk dihapus permanen"); },
    onError: (e) => toast.error(errMsg(e, "Gagal menghapus")),
  });

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form, harga: Number(form.harga) || 0,
        ukuran: form.ukuran.filter((u) => u.ukuran.trim()).map((u) => ({ ukuran: u.ukuran.trim(), stok: u.stok === "" || u.stok === null ? null : Number(u.stok) })) };
      return editing ? api.put(`/katalog/${editing.id}`, payload) : api.post("/katalog", payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["katalog"] }); toast.success(editing ? "Katalog diperbarui" : "Item ditambahkan"); setOpen(false); },
    onError: (e) => toast.error(errMsg(e, "Gagal menyimpan")),
  });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ nama: it.nama, harga: it.harga, unit: it.unit || "", aktif: it.aktif, ukuran: (it.ukuran || []).map((u) => ({ ukuran: u.ukuran, stok: u.stok == null ? "" : u.stok })) || [{ ukuran: "", stok: "" }] }); setOpen(true); };
  const setSize = (i, k, v) => setForm((f) => ({ ...f, ukuran: f.ukuran.map((u, idx) => idx === i ? { ...u, [k]: v } : u) }));
  const addSize = () => setForm((f) => ({ ...f, ukuran: [...f.ukuran, { ukuran: "", stok: "" }] }));
  const rmSize = (i) => setForm((f) => ({ ...f, ukuran: f.ukuran.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-5" data-testid="katalog-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <PageHeader icon={Package} title="Katalog Produk" subtitle={`${items.length} item · atur nama, harga, ukuran & stok. Kasir memilih dari katalog ini.`} />
        <Button data-testid="katalog-add-button" onClick={openNew} className="gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-800"><Plus className="h-4 w-4" /> Tambah Item</Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <div key={it.id} className={`glass rounded-2xl p-4 ${!it.aktif ? "opacity-60" : ""}`} data-testid={`katalog-card-${it.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-emerald-950 dark:text-emerald-50">{it.nama}</p>
                <p className="text-emerald-700 dark:text-emerald-400 font-bold">{rp(it.harga)}</p>
                {it.unit && <Badge className="border-0 bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300 mt-1">{it.unit}</Badge>}
                {!it.aktif && <Badge className="border-0 bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300 mt-1 ml-1">Nonaktif</Badge>}
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <Button variant="ghost" size="icon" data-testid={`katalog-edit-${it.id}`} onClick={() => openEdit(it)}><Pencil className="h-4 w-4" /></Button>
                {isSuper && <Button variant="ghost" size="icon" data-testid={`katalog-del-${it.id}`} onClick={() => { if (window.confirm(`Hapus permanen produk "${it.nama}"? Hanya untuk produk yang BELUM pernah ada transaksi. Bila sudah pernah terjual, gunakan Nonaktifkan agar riwayat tetap valid.`)) del.mutate(it.id); }}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(it.ukuran || []).map((u) => (
                <span key={u.ukuran} className="text-xs bg-white/60 dark:bg-white/5 rounded-lg px-2 py-1 text-slate-600 dark:text-slate-300">{u.ukuran}{u.stok != null && <span className="text-emerald-700 dark:text-emerald-400 font-medium"> · {u.stok}</span>}</span>
              ))}
              {(it.ukuran || []).length === 0 && <span className="text-xs text-slate-400">Belum ada ukuran</span>}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="glass rounded-2xl p-8 text-center text-slate-400 sm:col-span-2 lg:col-span-3">Belum ada item katalog.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="katalog-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Item Katalog" : "Tambah Item Katalog"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Nama Produk</Label><Input data-testid="katalog-nama-input" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="mis. Kaos Olahraga" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Harga (Rp)</Label><Input data-testid="katalog-harga-input" type="number" value={form.harga} onChange={(e) => setForm({ ...form, harga: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Unit Pengelola (opsional)</Label><Input data-testid="katalog-unit-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="mis. Koperasi / Ribath A" /></div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Ukuran & Stok (stok boleh kosong bila tak dilacak)</Label>
            {form.ukuran.map((u, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input placeholder="Ukuran (mis. L)" value={u.ukuran} data-testid={`katalog-size-${i}`} onChange={(e) => setSize(i, "ukuran", e.target.value)} className="flex-1" />
                <Input placeholder="Stok" type="number" value={u.stok} data-testid={`katalog-stok-${i}`} onChange={(e) => setSize(i, "stok", e.target.value)} className="w-24" />
                <Button variant="ghost" size="icon" onClick={() => rmSize(i)} data-testid={`katalog-rmsize-${i}`}><X className="h-4 w-4 text-rose-500" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="rounded-xl gap-1" data-testid="katalog-addsize" onClick={addSize}><Plus className="h-3.5 w-3.5" /> Tambah Ukuran</Button>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 pt-2"><Switch checked={form.aktif} onCheckedChange={(v) => setForm({ ...form, aktif: v })} data-testid="katalog-aktif-switch" /> Aktif (muncul di layar kasir)</label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="katalog-save-button" onClick={() => save.mutate()} disabled={save.isPending || !form.nama.trim()} className="bg-emerald-700 hover:bg-emerald-800">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
